import { useEffect, useRef } from 'react';
import { useAuthState } from '@/hooks/auth/useAuth';
import { isPostHogEnabled, posthog } from '@/lib/posthog/config';
import { createLogger } from '@/utils/logger';

const log = createLogger('PostHogUserContext');

/**
 * PostHogUserContext Component
 *
 * Mirrors SentryUserContext: syncs auth state into PostHog.
 *
 * PostHog is scoped to the CLIENT portal experience, so this component:
 * - identifies authenticated clients (by internal user id, never SSN/EIN)
 *   and starts session replay for them
 * - never identifies preparers (staff) — their sessions stay anonymous and
 *   their events are dropped by the `before_send` gate in the PostHog config
 * - resets the PostHog identity on logout so a shared browser (or a
 *   preparer testing a client login) never merges two people
 */
export function PostHogUserContext() {
  const { data: authState } = useAuthState();

  // Track whether THIS tab identified a client, so we only reset() on a
  // real identified→logged-out transition. Calling reset() on every
  // anonymous render would fragment anonymous visitors into new ids.
  const identifiedRef = useRef(false);

  const isAuthenticated = authState?.isAuthenticated;
  const user = authState?.user;
  const userType = authState?.userType;

  useEffect(() => {
    if (!isPostHogEnabled()) {
      return;
    }

    if (isAuthenticated && user?.id && userType === 'client') {
      posthog.identify(user.id, {
        email: user.email,
        user_type: 'client',
      });
      posthog.startSessionRecording();
      identifiedRef.current = true;
      log.debug('PostHog identified client user', { id: user.id });
    } else {
      posthog.stopSessionRecording();
      if (identifiedRef.current) {
        posthog.reset();
        identifiedRef.current = false;
        log.debug('PostHog identity reset');
      }
    }
  }, [isAuthenticated, user, userType]);

  // This component doesn't render anything
  return null;
}
